import { Toggle } from '@ph-react-ui/core';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../hooks/auth/useAuth';
import { mapDateToSimpleDate } from '../../../../../services/mapDateToSimpleDate';
import {
  DatePicker,
  Input,
} from '../../../../../utils/components/hoc-components';
import { Role } from '../../../../../utils/enums/role.enum';
import {
  getCommonValidators,
  VALIDATION as V,
} from '../../../../../utils/inputValidators';
import { PaymentSchemeSelect } from './PaymentSchemeSelect';

interface StudentSectionProps {
  formGroupName?: string;
  requiredFields?: {
    [key: string]: boolean;
  };
  inputDisabled?: boolean;
  canChangePaymentScheme?: boolean;
  canChangeNextYearsPaymentScheme?: boolean;
}

export function StudentSection({
  formGroupName = '',
  requiredFields,
  inputDisabled = false,
  canChangePaymentScheme = true,
  canChangeNextYearsPaymentScheme = true,
}: StudentSectionProps) {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { watch, setValue } = useFormContext();

  const isUsingRestaurant = watch(`${formGroupName}isUsingRestaurant`);

  const getFieldRules = (fieldName: string, validators = [V.MAX_LENGTH]) => ({
    ...getCommonValidators(validators),
    required: {
      value: requiredFields?.[fieldName] ?? false,
      message: t('ERROR.VALIDATION.REQUIRED'),
    },
  });

  return (
    <>
      <div className="mb-6">
        <Input
          name={`${formGroupName}name`}
          rules={getCommonValidators([V.MAX_LENGTH, V.REQUIRED])}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.PUPIL_NAME') ?? ''}
          label={t('USERS.DRAWER.LABELS.PUPIL_NAME')!}
          disabled={inputDisabled}
        />
      </div>

      <div className="mb-6">
        <Input
          name={`${formGroupName}egn`}
          rules={getFieldRules('egn', [V.NUMBER_PATTERN, V.EGN_MAX, V.EGN_MIN])}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.PUPIL_EGN') ?? ''}
          label={t('USERS.DRAWER.LABELS.PUPIL_EGN') ?? ''}
          disabled={inputDisabled}
        />
      </div>
      <div className="mb-6">
        <DatePicker
          name={`${formGroupName}birthDate`}
          rules={{
            required: {
              value: requiredFields?.['birthDate'] ?? false,
              message: t('ERROR.VALIDATION.REQUIRED'),
            },
          }}
          maxDate={mapDateToSimpleDate(new Date())}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.PUPIL_BIRTH_DATE') ?? ''}
          label={t('USERS.DRAWER.LABELS.PUPIL_BIRTH_DATE')!}
          disabled={inputDisabled}
        />
      </div>

      <div className="mb-6">
        <Input
          name={`${formGroupName}birthplace`}
          rules={getFieldRules('birthplace')}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.PUPIL_BIRTHPLACE') ?? ''}
          label={t('USERS.DRAWER.LABELS.PUPIL_BIRTHPLACE') ?? ''}
          disabled={inputDisabled}
        />
      </div>

      <div className="mb-6">
        <Input
          name={`${formGroupName}schoolYear`}
          rules={getFieldRules('schoolYear')}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.PUPIL_SCHOOL_YEAR') ?? ''}
          label={t('USERS.DRAWER.LABELS.PUPIL_SCHOOL_YEAR') ?? ''}
          disabled={inputDisabled}
        />
      </div>

      <div className="mb-6">
        <Input
          name={`${formGroupName}siblings`}
          rules={getFieldRules('siblings')}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.PUPIL_SIBLINGS') ?? ''}
          label={t('USERS.DRAWER.LABELS.PUPIL_SIBLINGS') ?? ''}
          disabled={inputDisabled}
        />
      </div>

      <div className="mb-6">
        <Input
          name={`${formGroupName}citizenship`}
          rules={getFieldRules('citizenship')}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.CITIZENSHIP') ?? ''}
          label={t('USERS.DRAWER.LABELS.CITIZENSHIP') ?? ''}
          disabled={inputDisabled}
        />
      </div>

      <div className="mb-6">
        <Input
          name={`${formGroupName}address`}
          rules={getFieldRules('address')}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.PUPIL_ADDRESS') ?? ''}
          label={t('USERS.DRAWER.LABELS.PUPIL_ADDRESS') ?? ''}
          disabled={inputDisabled}
        />
      </div>
      <div className="mb-6">
        <Input
          name={`${formGroupName}gpName`}
          rules={getFieldRules('gpName')}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.GP_NAME') ?? ''}
          label={t('USERS.DRAWER.LABELS.GP_NAME') ?? ''}
          disabled={inputDisabled}
        />
      </div>
      <div className="mb-6">
        <Input
          name={`${formGroupName}gpPhoneNumber`}
          rules={{
            maxLength: {
              value: 20,
              message: t('USERS.DRAWER.ERRORS.PHONE_LENGTH_ERROR'),
            },
          }}
          className="drawer__users__form__input"
          placeholder={t('USERS.DRAWER.PLACEHOLDERS.GP_PHONE_NUMBER') ?? ''}
          label={t('USERS.DRAWER.LABELS.GP_PHONE_NUMBER') ?? ''}
          disabled={inputDisabled}
        />
      </div>
      <div className="mb-6">
        <Toggle
          value={isUsingRestaurant ?? true}
          onChange={() =>
            setValue(`${formGroupName}isUsingRestaurant`, !isUsingRestaurant, {
              shouldDirty: true,
            })
          }
          disabled={inputDisabled || authState?.role !== Role.Admin}
        >
          {authState?.role !== Role.Admin && !isUsingRestaurant
            ? t('USERS.DRAWER.LABELS.NOT_USING_RESTAURANT')
            : t('USERS.DRAWER.LABELS.USES_RESTAURANT')}
        </Toggle>
      </div>
      {authState?.role === Role.Admin && (
        <PaymentSchemeSelect
          disabledCurrentYear={inputDisabled || !canChangePaymentScheme}
          disabledNextYear={inputDisabled || !canChangeNextYearsPaymentScheme}
        />
      )}
    </>
  );
}
